import classNames from 'classnames'
import { useEffect, useRef, useState } from 'react'
import Navbar from 'react-bootstrap/Navbar'
import ReactScrollspy from 'react-scrollspy'

import Icon from '@/components/Icon/Icon'
import localStorageService from '@/services/localStorage.service'
import { CardListType } from '@/types/page'

import styles from './Scrollspy.module.scss'

type SectionsProps = {
  cardlists: CardListType[]
  hidden?: boolean
  scrolledSection?: string
  setExpanded?: (expanded: boolean) => void
  setHidden?: (hidden: boolean) => void
}

const Sections = ({ cardlists, hidden, scrolledSection, setExpanded, setHidden }: SectionsProps) => {
  const ref = useRef<HTMLDivElement>(null)
  return (
    <div className={classNames(styles.sections, 'nav nav-pills flex-column')}>
      <div className={styles.iconRow}>
        <div
          className={styles.icon}
          tabIndex={0}
          ref={ref}
          role='button'
          onClick={() => {
            ref.current?.blur()
            setHidden && setHidden(!hidden)
          }}
        >
          <Icon icon={hidden ? 'arrowRight' : 'arrowLeft'} />
        </div>
      </div>
      {!hidden &&
        cardlists
          .filter((cardlist) => cardlist.cardviews.length > 0)
          .map((cardlist) => (
            <div className='nav-item' key={cardlist.tag}>
              <a
                className={classNames({ active: scrolledSection === cardlist.tag }, 'nav-link')}
                href={`#${cardlist.tag}`}
                onClick={() => setExpanded && setExpanded(false)}
              >
                {cardlist.header}
              </a>
            </div>
          ))}
    </div>
  )
}

type Props = {
  cardlists: CardListType[]
}

const Scrollspy = ({ cardlists }: Props) => {
  const ref = useRef<HTMLDivElement>(null)
  const [hidden, setHidden] = useState(false)
  const [expanded, setExpanded] = useState(false)
  const [scrolledSection, setScrolledSection] = useState()
  const [title, setTitle] = useState(
    cardlists.length > 0 ? `${cardlists[0]?.header} (${cardlists[0].cardviews.length})` : '',
  )

  useEffect(() => {
    const hidden = localStorageService.getJson('edhrec_scrollspy_hidden')
    if (hidden) {
      setHidden(hidden)
    }
  }, [])

  useEffect(() => {
    localStorage.setItem('edhrec_scrollspy_hidden', JSON.stringify(hidden))
  }, [hidden])

  if (cardlists.length < 2) {
    return null
  }

  const sections = (
    <Sections
      cardlists={cardlists}
      hidden={hidden}
      scrolledSection={scrolledSection}
      setExpanded={setExpanded}
      setHidden={setHidden}
    />
  )

  return (
    <div
      className={classNames('sticky-top', styles.outer)}
      ref={ref}
      id='scrollspy'
      role={hidden ? 'button' : undefined}
      onClick={(_e) => {
        if (hidden) {
          setHidden(false)
        }
        ref.current?.blur()
      }}
      tabIndex={0}
    >
      <ReactScrollspy
        className='navbar-nav'
        currentClassName='active'
        items={cardlists.map((cardlist) => cardlist.tag)}
        onUpdate={(el) => {
          if (el) {
            setScrolledSection(el.id as any)
            const cardlist = cardlists.find((cardlist) => cardlist.tag === el.id)
            setTitle(`${cardlist?.header} (${cardlist?.cardviews?.length})`)
          }
        }}
        offset={-80}
      />
      <div className={classNames(styles.mobile, 'shadow')}>
        <Navbar
          bg='dark'
          collapseOnSelect
          expand={false}
          expanded={expanded}
          onClick={() => setExpanded(!expanded)}
          variant='dark'
        >
          <Navbar.Brand href='#home'>{title}</Navbar.Brand>
          <Navbar.Toggle />
          <Navbar.Collapse>{sections}</Navbar.Collapse>
        </Navbar>
      </div>
      <div
        className={classNames(styles.desktop, {
          [styles.desktopHidden]: hidden,
        })}
      >
        {sections}
      </div>
    </div>
  )
}

export default Scrollspy
